import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Spinner } from 'react-bootstrap';
import { ListProps, SelectType } from '../protocols';
import { Option, Content } from '../styles';

export const Lista: React.FC<ListProps> = ({
  inputRef,
  setIsOpen,
  setFilter,
  hasNextPage,
  fetchNextPage,
  data,
  selected,
  setSelected,
  isFetching,
  changeSelected,
  queryName,
  queryClient,
  listaWidth,
  hideNoneOption = false,
  noneOptionLabel = 'Nenhuma',
}) => {
  const wrapperRef = useRef<any>(null);

  const useCloseOptions = (ref: any) => {
    useEffect(() => {
      const handleClickOutside = (event: any) => {
        if (ref.current && !ref.current.contains(event.target)) {
          if (inputRef) inputRef.current.value = '';
          setFilter('');
          queryClient.removeQueries(queryName);
          setIsOpen(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref]);
  };

  useCloseOptions(wrapperRef);

  const onScroll = async () => {
    if (!wrapperRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = wrapperRef.current;
    const zoomLevel = Math.round(window.devicePixelRatio * 100);
    const offset = Math.abs(100 - zoomLevel);

    if (scrollTop + clientHeight >= scrollHeight - offset) {
      if (hasNextPage) await fetchNextPage();
    }
  };

  const handleClick = useCallback(
    (option: SelectType) => {
      option.label = `${option.label}`;
      setSelected(option);
      changeSelected(option);
      setIsOpen(false);
    },
    [changeSelected, setIsOpen, setSelected],
  );

  const flatItems: any[] = useMemo(() => {
    if (!data) return [];
    return data.pages?.flatMap((p: any) => p?.data || []) ?? [];
  }, [data]);

  const applySelectionUX = (item: { label: string; value: number }) => {
    if (!inputRef) return;
    inputRef.current.placeholder = `${item.label}`;
    inputRef.current.value = '';
    queryClient.removeQueries(queryName);
    setFilter('');
  };

  const renderNoneOption = () => {
    if (hideNoneOption) return null;

    const noneItem: SelectType = {
      value: -1,
      label: noneOptionLabel,
    };

    const isSelected = selected?.value === -1;

    return (
      <Option
        key="__none__"
        className={isSelected ? 'selected' : ''}
        onClick={() => {
          handleClick(noneItem);
          applySelectionUX(noneItem);
        }}
        onKeyPress={() => {
          handleClick(noneItem);
          applySelectionUX(noneItem);
        }}
      >
        {noneOptionLabel}
      </Option>
    );
  };

  const renderItems = () =>
    flatItems.map((item: any) => (
      <Option
        key={item.value}
        className={selected?.value === item.value ? 'selected' : ''}
        onClick={() => {
          handleClick(item);
          applySelectionUX(item);
        }}
        onKeyPress={() => {
          handleClick(item);
          applySelectionUX(item);
        }}
      >
        {`${item.label}`}
      </Option>
    ));

  const renderContent = () => {
    if (!data) return null;

    const mustShowEmpty =
      data.pages?.[0]?.message === 'url not found' ||
      flatItems.length <= 0 ||
      (data.pages?.[0]?.message === 'Registros encontrados' &&
        flatItems.length <= 0);

    if (mustShowEmpty) {
      return (
        <>
          {renderNoneOption()}
          {!hideNoneOption ? null : (
            <Option className="not-found">Nenhum registro encontrado.</Option>
          )}
          {!hideNoneOption ? (
            <Option className="not-found">Nenhum registro encontrado.</Option>
          ) : null}
        </>
      );
    }

    return (
      <>
        {renderNoneOption()}
        {renderItems()}
      </>
    );
  };

  return (
    <Content listaWidth={listaWidth}>
      <ul ref={wrapperRef} onScroll={onScroll}>
        {data
          ? renderContent()
          : !isFetching && (
              <Option className="not-found">
                Digite ao menos 3 caracteres para pesquisar.
              </Option>
            )}

        {isFetching && (
          <Option className="not-found">
            <Spinner animation="border" size="sm" className="spinner" />
          </Option>
        )}
      </ul>
    </Content>
  );
};
